import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceApi, type Message } from '../api/workspace';
import { Loading, PageError } from '../components/State';
import { isApiError } from '../lib/apiClient';
import { fmtDateTime } from '../lib/format';
import './MessageThreadPage.css';

type Recipient = Message['recipientRole'];

const RECIPIENTS: { value: Recipient; label: string }[] = [
  { value: 'both', label: 'Caregiver & youth' },
  { value: 'caregiver', label: 'Caregiver only' },
  { value: 'youth', label: 'Youth only' },
];

export function MessageThreadPage() {
  const { connectionId = '' } = useParams();
  const youthName = (useLocation().state as { youthName?: string } | null)?.youthName ?? 'Conversation';
  const qc = useQueryClient();

  const [text, setText] = useState('');
  const [recipient, setRecipient] = useState<Recipient>('both');
  const endRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['thread', connectionId],
    queryFn: () => workspaceApi.messages(connectionId),
    enabled: !!connectionId,
    refetchInterval: 20_000,
  });

  const send = useMutation({
    mutationFn: () => workspaceApi.sendMessage(connectionId, text.trim(), recipient),
    onSuccess: () => {
      setText('');
      refetch();
      qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [data?.length]);

  const grouped = useMemo(() => groupByDay(data ?? []), [data]);

  if (isLoading) return <Loading />;

  return (
    <div className="thread">
      <div className="thread__head">
        <Link to="/messages" className="yp__back">
          ‹ Messages
        </Link>
        <h1>{youthName}</h1>
      </div>

      {isError ? (
        <PageError error={error} onRetry={() => refetch()} />
      ) : (
        <div className="thread__scroll">
          {grouped.length === 0 && (
            <p className="muted" style={{ textAlign: 'center', marginTop: 40 }}>
              No messages yet. Say hello.
            </p>
          )}
          {grouped.map((g) => (
            <div key={g.day}>
              <div className="thread__daysep">{g.day}</div>
              {g.items.map((m) => {
                const mine = m.senderRole === 'care_team';
                return (
                  <div key={m.id} className={`bubble${mine ? ' bubble--mine' : ''}`}>
                    <div className="bubble__text">{m.text}</div>
                    <div className="bubble__meta">
                      {fmtDateTime(m.createdAt)}
                      {mine && m.recipientRole !== 'both' ? ` · to ${m.recipientRole}` : ''}
                      {mine ? (m.readAt ? ' · read' : ' · sent') : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={endRef} />
        </div>
      )}

      <form
        className="thread__composer"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) send.mutate();
        }}
      >
        <select
          value={recipient}
          onChange={(e) => setRecipient(e.target.value as Recipient)}
          className="thread__recipient"
        >
          {RECIPIENTS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <div className="thread__inputrow">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a message…"
            maxLength={2000}
          />
          <button className="btn" type="submit" disabled={!text.trim() || send.isPending}>
            {send.isPending ? '…' : 'Send'}
          </button>
        </div>
        {send.isError && (
          <p className="error-text">
            {isApiError(send.error) ? send.error.message : 'Could not send.'}
          </p>
        )}
      </form>
    </div>
  );
}

function groupByDay(msgs: Message[]) {
  const out: { day: string; items: Message[] }[] = [];
  for (const m of msgs) {
    const day = new Date(m.createdAt).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
    const last = out[out.length - 1];
    if (last && last.day === day) last.items.push(m);
    else out.push({ day, items: [m] });
  }
  return out;
}
